/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from "react-router-dom";
import { Container, Row, Col } from 'reactstrap';
import * as default_data from '../config.js';
import { getToken } from '../AuthService.js';
import { TextField, Button } from '@mui/material';
import AdminHorizontalMenu from './AdminHorizontalMenu.js';
import SelectOrganization from '../SuperAdmin/SelectOrganization.js';
import { getBannerColor } from '../Utils/classification_helper.js';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TableContainer,
  Paper
} from '@mui/material';

const UsersList = ({ type, full_stats }) => {
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchParams] = useSearchParams();
  const [loadingText, setLoadingText] = useState("Loading...");
  const [PurchasedData, setPurchasedData] = useState(null);
  const [UsedData, setUsedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [emailFilter, setEmailFilter] = useState('');
  const [paidFilter, setPaidFilter] = useState('');
  const [hideBannedFilter, setHideBannedFilter] = useState('');

  // Sorting
  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  const itemsPerPage = 100;
  let loaded_static = false;

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');

  // Manage local states for updating tokens
  const [maxTokensValues, setMaxTokensValues] = useState({});
  const [maxTrainTokensValues, setMaxTrainTokensValues] = useState({});

  // MUI Table columns config
  const columns = [
    { id: 'id', label: 'ID', sortable: true, show: true },
    { id: 'organization_id', label: 'Org', sortable: true, show: (type === 'superadmin') },
    { id: 'company', label: 'Company', sortable: true, show: true },
    { id: 'first_name', label: 'First name', sortable: true, show: true },
    { id: 'last_name', label: 'Last name', sortable: true, show: true },
    { id: 'email', label: 'Email', sortable: true, show: true },
    { id: 'x509_cn', label: 'CAC', sortable: true, show: true },
    { id: 'mfa_secret', label: 'MFA', sortable: true, show: true },
    { id: 'country', label: 'Country', sortable: true, show: true },
    { id: 'type', label: 'Type', sortable: true, show: true },
    { id: 'paid', label: 'Paid', sortable: true, show: true },
    { id: 'banned', label: 'Banned', sortable: true, show: true },
    { id: 'email_verified', label: 'Email verified', sortable: true, show: true },
    { id: 'max_tokens', label: 'Max tokens', sortable: true, show: true },
    { id: 'max_train_tokens', label: 'Max train tokens', sortable: true, show: true },
    {
      id: 'total_tokens',
      label: full_stats === 1 ? 'Total inf. tokens' : 'Total tokens',
      sortable: false,
      show: true
    },
    {
      id: 'total_train_tokens',
      label: 'Total train tokens',
      sortable: false,
      show: (full_stats === 1)
    },
    {
      id: 'total_tokens_current_month',
      label: 'Month inf. tokens',
      sortable: false,
      show: (full_stats === 1)
    },
    {
      id: 'total_train_tokens_current_month',
      label: 'Month train tokens',
      sortable: false,
      show: (full_stats === 1)
    },
    {
      id: 'total_tokens_previous_month',
      label: 'Month N-1 inf. tokens',
      sortable: false,
      show: (full_stats === 1)
    },
    {
      id: 'total_train_tokens_previous_month',
      label: 'Month N-1 train tokens',
      sortable: false,
      show: (full_stats === 1)
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (loaded || loaded_static) {
        return;
      }
      setLoaded(true);
      loaded_static = true;

      setIsLoading(true);

      if (type === 'superadmin') {
        await loadOrganizations();
      }

      let use_email = emailFilter;
      let use_organization_id = selectedOrganization;

      const orgId = searchParams.get('organization_id');
      const email = searchParams.get('email');

      if (orgId && !isNaN(orgId)) {
        setSelectedOrganization(orgId);
        use_organization_id = orgId;
      }

      if (email) {
        setEmailFilter(email);
        use_email = email;
      }

      await loadUsers(use_email, use_organization_id);
      await loadUserCount(use_email, use_organization_id);

      await loadPurchasedData();
      await loadUsedData();

      setIsLoading(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, emailFilter, paidFilter, hideBannedFilter]);

  var last_page_number = '';
  
  useEffect(() => {
    if(currentPage === last_page_number) {
      return;
    }
    else {
      last_page_number = currentPage;
    }
    loadUsers(emailFilter, selectedOrganization);
  }, [currentPage]);

  useEffect(() => {
    if (loaded) {
      loadUsers(emailFilter, selectedOrganization);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDirection]);

  // Organization loading (superadmin only)
  async function loadOrganizations() {
    try {
      const token = getToken();
      if (!token) {
        return;
      }
      const response = await fetch(
        default_data.default_user_service_url_no_cac + '/superadmin-get-all-organizations',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-tokens': `${token}`
          }
        }
      );
      if (!response.ok) {
        throw new Error('An error occurred while getting organizations.');
      }
      const data = await response.json();
      if (data.status != 200) {
        return;
      }
      const sortedOrganizations = data.response.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setOrganizations(sortedOrganizations);
    } catch (error) {
      console.log(error);
    }
  }

  // Load total user count for pagination
  const loadUserCount = (emailFilter, organization_id) => {
    let cur_organization_id = organization_id;
    if (cur_organization_id && cur_organization_id !== '' && !isNaN(cur_organization_id)) {
      cur_organization_id = parseInt(cur_organization_id, 10);
    }

    const token = getToken();
    if (!token) {
      return;
    }

    fetch(default_data.default_user_service_url_no_cac + '/' + type + '-get-user-count', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({
        email: emailFilter,
        paid: paidFilter,
        hide_banned: hideBannedFilter,
        organization_id: cur_organization_id
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        if (data.status != 200) {
          window.location = '/login';
          return;
        }
        setTotalCount(data.response.total_count);
      })
      .catch(error => {
        console.log(error);
      });
  };

  // For verifying user email
  function updateUserVerified(user_id) {
    const token = getToken();
    if (!token) return;

    setIsLoading(true);
    const scrollPosition = window.scrollY;

    fetch(default_data.default_user_service_url_no_cac + '/' + type + '-update-user-verified', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({ id: user_id })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while updating.');
      })
      .then(data => {
        setIsLoading(false);
        if (data.status != 200) {
          window.location = '/login';
          return;
        }
        // Update local state
        const userIndex = users.findIndex(user => user.id === user_id);
        const newUsers = [...users];
        newUsers[userIndex].email_verified = 'True';
        setUsers(newUsers);

        window.scrollTo(0, scrollPosition);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  }

  // For updating max tokens
  function updateUserTokens(user_id, tokens) {
    const token = getToken();
    if (!token) return;

    setIsLoading(true);
    const scrollPosition = window.scrollY;

    fetch(default_data.default_user_service_url_no_cac + '/' + type + '-update-user-tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({
        id: user_id,
        tokens: tokens
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while updating tokens.');
      })
      .then(data => {
        if (data.status != 200) {
          if (data.status == 401) {
            setErrorMessage(data.response);
            setTimeout(() => {
              setErrorMessage("");
            }, 15000);
          } else {
            window.location = '/login';
            return;
          }
        }
        setIsLoading(false);

        // Update local state
        const userIndex = users.findIndex(user => user.id === user_id);
        const newUsers = [...users];
        newUsers[userIndex].max_tokens = tokens;
        setUsers(newUsers);

        window.scrollTo(0, scrollPosition);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  }

  // For updating train tokens
  function updateUserTrainTokens(user_id, tokens) {
    const token = getToken();
    if (!token) return;

    setIsLoading(true);
    const scrollPosition = window.scrollY;

    fetch(default_data.default_user_service_url_no_cac + '/' + type + '-update-user-train-tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({
        id: user_id,
        tokens: tokens
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while updating training tokens.');
      })
      .then(data => {
        if (data.status != 200) {
          if (data.status == 401) {
            setErrorMessage(data.response);
            setTimeout(() => {
              setErrorMessage("");
            }, 15000);
          } else {
            window.location = '/login';
            return;
          }
        }
        setIsLoading(false);

        // Update local state
        const userIndex = users.findIndex(user => user.id === user_id);
        const newUsers = [...users];
        newUsers[userIndex].max_train_tokens = tokens;
        setUsers(newUsers);

        window.scrollTo(0, scrollPosition);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  }

  // For updating paid (force paid or remove paid)
  function updateUserPaid(user_id, paid, testing = 0) {
    const token = getToken();
    if (!token) return;

    setIsLoading(true);
    const scrollPosition = window.scrollY;

    let paidVal = paid === 0 ? 0 : 1;

    fetch(default_data.default_user_service_url_no_cac + '/' + type + '-update-user-paid', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({
        id: user_id,
        paid: paidVal,
        testing: testing
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while updating paid.');
      })
      .then(data => {
        setIsLoading(false);
        if (data.status != 200) {
          if (data.status == 401) {
            setErrorMessage(data.response);
            setTimeout(() => {
              setErrorMessage("");
            }, 15000);
          } else {
            window.location = '/login';
            return;
          }
        }
        loadUsers(emailFilter, selectedOrganization);
        window.scrollTo(0, scrollPosition);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  }

  // For updating admin status (superadmin only)
  function updateUserAdmin(user_id, admin) {
    const token = getToken();
    if (!token) return;

    setIsLoading(true);
    const scrollPosition = window.scrollY;

    let adminVal = admin === 0 ? 0 : 1;

    fetch(default_data.default_user_service_url_no_cac + '/superadmin-update-user-admin', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({
        id: user_id,
        admin: adminVal
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while updating admin status.');
      })
      .then(data => {
        setIsLoading(false);
        if (data.status != 200) {
          if (data.status == 401) {
            setErrorMessage(data.response);
            setTimeout(() => {
              setErrorMessage("");
            }, 15000);
          } else {
            window.location = '/login';
            return;
          }
        }
        loadUsers(emailFilter, selectedOrganization);
        window.scrollTo(0, scrollPosition);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  }

  // For banning/unbanning users
  function updateUserBanned(user_id, banned) {
    const token = getToken();
    if (!token) return;

    setIsLoading(true);
    const scrollPosition = window.scrollY;

    let bannedVal = banned === 0 ? 0 : 1;

    fetch(default_data.default_user_service_url_no_cac + '/' + type + '-update-user-banned', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({
        id: user_id,
        banned: bannedVal
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while updating banned status.');
      })
      .then(data => {
        setIsLoading(false);
        if (data.status != 200) {
          if (data.status == 401) {
            setErrorMessage(data.response);
            setTimeout(() => {
              setErrorMessage("");
            }, 15000);
          } else {
            window.location = '/login';
            return;
          }
        }
        // If unbanned, reload. Otherwise, just update locally.
        if (bannedVal === 0) {
          loadUsers(emailFilter, selectedOrganization);
        } else {
          const userIndex = users.findIndex(user => user.id === user_id);
          const newUsers = [...users];
          newUsers[userIndex].banned = true;
          newUsers[userIndex].max_tokens = 0;
          newUsers[userIndex].max_train_tokens = 0;
          setUsers(newUsers);
        }
        window.scrollTo(0, scrollPosition);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  }

  // Main user loading function
  const loadUsers = (emailFilter, organization_id) => {
    const token = getToken();
    if (!token) return;

    setIsLoading(true);
    setLoadingText('Loading...');

    let cur_organization_id = organization_id;
    if (cur_organization_id && cur_organization_id !== '' && !isNaN(cur_organization_id)) {
      cur_organization_id = parseInt(cur_organization_id, 10);
    }

    fetch(default_data.default_user_service_url_no_cac + '/' + type + '-get-all-users-with-tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({
        full: full_stats,
        hide_banned: hideBannedFilter,
        email: emailFilter,
        start: (currentPage - 1) * itemsPerPage,
        paid: paidFilter,
        limit: itemsPerPage,
        organization_id: cur_organization_id,
        sort_by: sortBy,
        sort_direction: sortDirection
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        if (data.status != 200) {
          window.location = '/login';
          return;
        }
        const result = data.response;
        setUsers(result);

        // Initialize or update local token states
        const newMaxTokensValues = {};
        const newMaxTrainTokensValues = {};
        result.forEach(u => {
          newMaxTokensValues[u.id] = u.max_tokens;
          newMaxTrainTokensValues[u.id] = u.max_train_tokens;
        });
        setMaxTokensValues(newMaxTokensValues);
        setMaxTrainTokensValues(newMaxTrainTokensValues);

        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  };

  // Load purchased data (admin only)
  const loadPurchasedData = () => {
    if (type !== 'admin') {
      return;
    }
    const token = getToken();
    if (!token) {
      return;
    }

    setIsLoading(true);
    setLoadingText('Loading...');

    fetch(default_data.default_user_service_url_no_cac + '/admin-total-purchased-tokens-organization', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({})
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        if (data.status != 200) {
          window.location = '/login';
          return;
        }
        setPurchasedData(data.response);
        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  };

  // Load used data (admin only)
  const loadUsedData = () => {
    if (type !== 'admin') {
      return;
    }
    const token = getToken();
    if (!token) {
      return;
    }

    setIsLoading(true);
    setLoadingText('Loading...');

    fetch(default_data.default_user_service_url_no_cac + '/admin-used-tokens-for-organization', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({})
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        if (data.status != 200) {
          window.location = '/login';
          return;
        }
        setUsedData(data.response);
        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  };

  // Page change handler
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Sorting handler
  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
  };

  // Clear filter
  const handleClearFilter = () => {
    setEmailFilter('');
    setSelectedOrganization('');
    handleFilterChange('', '');
  };

  // Filter change
  const handleFilterChange = (email, organization_id) => {
    setCurrentPage(1);
    loadUsers(email, organization_id);
    loadUserCount(email, organization_id);
  };

  const findOrganizationName = (organization_id) => {
    if (organization_id === null) {
      return;
    }
    const organization = organizations.find(org => org.id === organization_id);
    return organization ? organization.name : '';
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const toBooleanString = (value) => {
    return value ? 'True' : 'False';
  };

  // Simple overlay for loading
  const loading_styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    },
    loadingText: {
      color: '#fff',
      fontSize: '24px'
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '100vh' }}>
      {/* Top banner/header message */}
      <div className={`header-message ${getBannerColor(true)}`}>
        { default_data.default_banner_override && default_data.default_banner_override !== ''
          ? default_data.default_banner_override
          : 'CUI'
        }
      </div>

      {/* Main flex container to fill remaining space */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Bootstrap Container as another flex child */}
        <Container fluid style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header row (logo, etc.) */}
          <Row className="justify-content-center reg-page-mode-header">
            <Col xs="12" md="12" lg="12" className="d-flex align-items-center">
              <a href="/" className="d-flex align-items-center">
                <img src="/logo.png" alt="Ask Sage" className="logo" />
              </a>
              <h1 className="user-list-h1">
                {type === 'admin' ? "Organization's Users" : 'Tenant Users'}
              </h1>
            </Col>
          </Row>

          {/* Main content row with filters, table, pagination, etc. */}
          <Row
            className="user-list-reg-page-mode-content justify-content-center reg-page-mode-content"
            style={{ padding: '10px !important', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <Col xs="12" md="12" lg="12" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Filters / Admin stats */}
              <div style={{ fontSize: '0.8em !important', margin: '10px' }}>
                <AdminHorizontalMenu type={type} full_stats={full_stats} />

                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                  {PurchasedData !== null && (
                    <div className="org-tokens-data">
                      Total Tokens Purchased: {PurchasedData.total_tokens.toLocaleString(navigator.language)} - 
                      Total Training Tokens Purchased: {PurchasedData.total_train_tokens.toLocaleString(navigator.language)}
                    </div>
                  )}
                  {UsedData !== null && (
                    <div className="org-tokens-data">
                      Total Tokens Assigned: {UsedData.total_tokens.toLocaleString(navigator.language)} - 
                      Total Training Tokens Assigned: {UsedData.total_train_tokens.toLocaleString(navigator.language)}
                    </div>
                  )}
                </div>

                {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                <span style={{fontSize: '0.8em'}}>Users: {totalCount}&nbsp;-&nbsp;</span>
                {type === 'superadmin' && (
                  <SelectOrganization
                    organizations={organizations}                    
                    small={true}
                    selectedOrganization={selectedOrganization}
                    setSelectedOrganization={setSelectedOrganization}
                    handleFilterChange={handleFilterChange}
                    emailFilter={emailFilter}
                  />
                )}

                <TextField
                  label='Email Filter'
                  variant="outlined"
                  size='small'
                  value={emailFilter}
                  InputProps={{
                    style: {
                      height: '32px', // Reduce the height
                      fontSize: '0.8rem' // Smaller font size
                    }
                  }}
                  InputLabelProps={{
                    style: {
                      fontSize: '0.8rem', // Smaller label font size
                    },
                    shrink: true
                  }}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleFilterChange(emailFilter, selectedOrganization);
                    }
                  }}
                />
                <Button
                  style={{ marginLeft: '10px' }}
                  onClick={() => handleFilterChange(emailFilter, selectedOrganization)}
                >
                  Apply Filters
                </Button>
                <Button
                  style={{ marginLeft: '10px' }}
                  onClick={handleClearFilter}
                >
                  Clean Filters
                </Button>
              </div>

              {/* Table wrapper that flexes & scrolls */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginTop: '10px', marginBottom: '10px' }}>
                <TableContainer
                  component={Paper}
                  className="special-scrollbar user-list-table-parent"
                  style={{
                    flex: 1,
                    overflow: 'auto', // Both vertical and horizontal scrolling
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Table className="user-list-table" size="small">
                    <TableHead>
                      <TableRow>
                        {columns
                          .filter(col => col.show)
                          .map(col => (
                            <TableCell className="compact-cell" key={col.id}>
                              {col.sortable ? (
                                <TableSortLabel
                                  active={sortBy === col.id}
                                  direction={sortBy === col.id ? sortDirection : 'asc'}
                                  onClick={() => handleSort(col.id)}
                                >
                                  {col.label}
                                </TableSortLabel>
                              ) : (
                                col.label
                              )}
                            </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length === 0 && (
                        <TableRow>
                          <TableCell className="compact-cell" colSpan={columns.filter(c => c.show).length}>
                            Loading...
                          </TableCell>
                        </TableRow>
                      )}
                      {users.map((info) => (
                        <TableRow key={info.id}>
                          {columns.find(c => c.id === 'id')?.show && (
                            <TableCell className="compact-cell">
                              <a href={`/${type}/get-logs?user_id=${info.id}`} target="_blank" rel="noreferrer">
                                {info.id}
                              </a>
                            </TableCell>
                          )}

                          {type === 'superadmin' && columns.find(c => c.id === 'organization_id')?.show && (
                            <TableCell className="compact-cell">
                              {findOrganizationName(info.organization_id)}
                            </TableCell>
                          )}

                          {columns.find(c => c.id === 'company')?.show && (
                            <TableCell className="compact-cell">{info.company}</TableCell>
                          )}

                          {columns.find(c => c.id === 'first_name')?.show && (
                            <TableCell className="compact-cell">{info.first_name}</TableCell>
                          )}

                          {columns.find(c => c.id === 'last_name')?.show && (
                            <TableCell className="compact-cell">{info.last_name}</TableCell>
                          )}

                          {columns.find(c => c.id === 'email')?.show && (
                            <TableCell className="compact-cell">{info.email}</TableCell>
                          )}

                          {columns.find(c => c.id === 'x509_cn')?.show && (
                            <TableCell className="compact-cell">{toBooleanString(info.x509_cn)}</TableCell>
                          )}

                          {columns.find(c => c.id === 'mfa_secret')?.show && (
                            <TableCell className="compact-cell">{toBooleanString(info.mfa_secret)}</TableCell>
                          )}

                          {columns.find(c => c.id === 'country')?.show && (
                            <TableCell className="compact-cell">{info.country}</TableCell>
                          )}

                          {columns.find(c => c.id === 'type')?.show && (
                            <TableCell className="compact-cell">
                              {info.type}
                              <br />
                              {type === 'superadmin' && (
                                info.type === 'user' ? (
                                  <a href="#0" onClick={() => updateUserAdmin(info.id, 1)}>Add Admin</a>
                                ) : info.type === 'admin' ? (
                                  <a href="#0" onClick={() => updateUserAdmin(info.id, 0)}>Remove Admin</a>
                                ) : null
                              )}
                            </TableCell>
                          )}

                          {columns.find(c => c.id === 'paid')?.show && (
                            <TableCell className="compact-cell">
                              {info.paid ? (
                                <span>
                                  True<br />
                                  <a href="#0" onClick={() => updateUserPaid(info.id, 0)}>Force</a>
                                </span>
                              ) : (
                                <span>
                                  False<br />
                                  {type === 'superadmin' ? (
                                    <>
                                      <a href="#0" onClick={() => updateUserPaid(info.id, 1)}>Force</a><br />
                                      <a href="#0" onClick={() => updateUserPaid(info.id, 1, 1)}>Force Trial</a>
                                    </>
                                  ) : (
                                    <a href="#0" onClick={() => updateUserPaid(info.id, 1)}>Force</a>
                                  )}
                                </span>
                              )}
                            </TableCell>
                          )}

                          {columns.find(c => c.id === 'banned')?.show && (
                            <TableCell className="compact-cell">
                              {info.banned ? (
                                <span>
                                  True<br />
                                  <a href="#0" onClick={() => updateUserBanned(info.id, 0)}>Force</a>
                                </span>
                              ) : (
                                <span>
                                  False<br />
                                  <a href="#0" onClick={() => updateUserBanned(info.id, 1)}>Force</a>
                                </span>
                              )}
                            </TableCell>
                          )}

                          {columns.find(c => c.id === 'email_verified')?.show && (
                            <TableCell className="compact-cell">
                              {info.email_verified === 'True' ? 'True' : (
                                <a href="#0" onClick={() => updateUserVerified(info.id)}>Force</a>
                              )}
                            </TableCell>
                          )}

                          {columns.find(c => c.id === 'max_tokens')?.show && (
                            <TableCell className="compact-cell">
                              <input
                                type="number"
                                min="1"
                                style={{ width: '90px' }}
                                value={maxTokensValues[info.id] || ''}
                                onChange={event => {
                                  setMaxTokensValues({
                                    ...maxTokensValues,
                                    [info.id]: event.target.value
                                  });
                                }}
                              />
                              <br />
                              <a href="#0" onClick={() => updateUserTokens(info.id, maxTokensValues[info.id])}>
                                Save
                              </a>
                            </TableCell>
                          )}

                          {columns.find(c => c.id === 'max_train_tokens')?.show && (
                            <TableCell className="compact-cell">
                              <input
                                type="number"
                                min="1"
                                style={{ width: '90px' }}
                                value={maxTrainTokensValues[info.id] || ''}
                                onChange={event => {
                                  setMaxTrainTokensValues({
                                    ...maxTrainTokensValues,
                                    [info.id]: event.target.value
                                  });
                                }}
                              />
                              <br />
                              <a href="#0" onClick={() => updateUserTrainTokens(info.id, maxTrainTokensValues[info.id])}>
                                Save
                              </a>
                            </TableCell>
                          )}

                          {columns.find(c => c.id === 'total_tokens')?.show && (
                            <TableCell className="compact-cell">
                              {navigator.language.includes("en") ||
                              navigator.language.includes("US") ||
                              navigator.language.includes("us")
                                ? (full_stats === 1
                                    ? info.total_tokens?.toLocaleString(navigator.language)
                                    : info.total_tokens_all?.toLocaleString(navigator.language))
                                : (full_stats === 1
                                    ? info.total_tokens
                                    : info.total_tokens_all)
                              }
                            </TableCell>
                          )}

                          {full_stats === 1 && columns.find(c => c.id === 'total_train_tokens')?.show && (
                            <TableCell className="compact-cell">
                              {navigator.language.includes("en") ||
                              navigator.language.includes("US") ||
                              navigator.language.includes("us")
                                ? info.total_train_tokens?.toLocaleString(navigator.language)
                                : info.total_train_tokens}
                            </TableCell>
                          )}

                          {full_stats === 1 && columns.find(c => c.id === 'total_tokens_current_month')?.show && (
                            <TableCell className="compact-cell">
                              {navigator.language.includes("en") ||
                              navigator.language.includes("US") ||
                              navigator.language.includes("us")
                                ? info.total_tokens_current_month?.toLocaleString(navigator.language)
                                : info.total_tokens_current_month}
                            </TableCell>
                          )}

                          {full_stats === 1 && columns.find(c => c.id === 'total_train_tokens_current_month')?.show && (
                            <TableCell className="compact-cell">
                              {navigator.language.includes("en") ||
                              navigator.language.includes("US") ||
                              navigator.language.includes("us")
                                ? info.total_train_tokens_current_month?.toLocaleString(navigator.language)
                                : info.total_train_tokens_current_month}
                            </TableCell>
                          )}

                          {full_stats === 1 && columns.find(c => c.id === 'total_tokens_previous_month')?.show && (
                            <TableCell className="compact-cell">
                              {navigator.language.includes("en") ||
                              navigator.language.includes("US") ||
                              navigator.language.includes("us")
                                ? info.total_tokens_previous_month?.toLocaleString(navigator.language)
                                : info.total_tokens_previous_month}
                            </TableCell>
                          )}

                          {full_stats === 1 && columns.find(c => c.id === 'total_train_tokens_previous_month')?.show && (
                            <TableCell className="compact-cell">
                              {navigator.language.includes("en") ||
                              navigator.language.includes("US") ||
                              navigator.language.includes("us")
                                ? info.total_train_tokens_previous_month?.toLocaleString(navigator.language)
                                : info.total_train_tokens_previous_month}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

              {/* Pagination below table */}
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />

              {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
            </Col>
          </Row>

          {/* Footer row */}
          <Row className="justify-content-center reg-page-mode-footer">
            <Col xs="12" md="12" lg="12">
              Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
            </Col>
          </Row>
        </Container>
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div style={loading_styles.overlay}>
          <span style={loading_styles.loadingText}>{loadingText}</span>
        </div>
      )}
    </div>
  );
};

// Simple Pagination component
const Pagination = ({ totalPages, currentPage, handlePageChange }) => {
  const [inputPage, setInputPage] = useState('');

  const handleInputChange = (e) => {
    setInputPage(e.target.value);
  };

  const handleInputSubmit = () => {
    const pageNumber = parseInt(inputPage, 10);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      handlePageChange(pageNumber);
    }
    setInputPage('');
  };

  const startPage = Math.max(1, currentPage - 5);
  const endPage = Math.min(totalPages, currentPage + 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
        {Array.from({ length: endPage - startPage + 1 }, (_, index) => (
          <Button
            style={{ fontSize: '0.8em', height: '25px', padding: '0', marginLeft: '10px', display: 'inline' }}
            variant="contained"
            size="small"
            key={startPage + index}
            onClick={() => handlePageChange(startPage + index)}
            disabled={currentPage === startPage + index}
          >
            {startPage + index}
          </Button>
        ))}
      </div>
      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
        <TextField
          label="Go to page"
          variant="outlined"
          size="small"
          InputProps={{
            style: {
              height: '32px', // Reduce the height
              fontSize: '0.8rem' // Smaller font size
            }
          }}
          InputLabelProps={{
            style: {
              fontSize: '0.8rem', // Smaller label font size
            },
            shrink: true
          }}
          value={inputPage}
          onChange={handleInputChange}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleInputSubmit();
            }
          }}
        />
        <Button
          style={{ marginLeft: '10px' }}
          variant="contained"
          size="small"
          onClick={handleInputSubmit}
        >
          Go
        </Button>
      </div>
    </div>
  );
};

export { UsersList };
